import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Calendar, Clock, ChevronRight } from 'lucide-react';
import image from '../components/images/RESO2.svg';
import RESO3 from './images/RESO31.svg';
import './style/button.css';

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  const fetchPosts = useCallback(async (page = 1, search = '') => {
    try {
      setIsLoading(true);
      const response = await fetch(`http://34.47.145.31/api/blogs?page=${page}&limit=9${search ? `&search=${search}` : ''}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch posts');

      setPosts(data.blogs);
      setCurrentPage(data.currentPage);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError('Failed to load blog posts');
      console.error('Error fetching posts:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchPosts(1, searchTerm);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, fetchPosts]);

  useEffect(() => {
    fetchPosts(currentPage, searchTerm);
  }, [currentPage, searchTerm, fetchPosts]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Oops!</h2>
        <p className="text-gray-600 dark:text-gray-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section with Black Background */}
      <section className="relative bg-black min-h-[80vh] px-8 flex items-center">
        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="text-left space-y-8">
            <h1 className="text-6xl font-extrabold text-white leading-tight">
              Hey, we're <span className="text-yellow-400">Blogy</span>.<br />
              See our thoughts, stories and ideas.
            </h1>
            <div className="h-1 w-24 bg-yellow-400"></div>
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-white">
                <span className="text-yellow-400">Start Blogging.</span><br />
                Build Your Brand.
              </h2>
              <ul className="text-gray-200 space-y-2 list-disc list-inside">
                <li>Share your story & expertise. Grow your audience.</li>
                <li>Monetize your passion.</li>
              </ul>
            </div>
            <button 
              onClick={() => navigate('/write')}
              className="px-8 py-4 bg-yellow-400 text-gray-900 rounded-full font-bold text-lg 
                       hover:bg-yellow-300 transition-all transform hover:scale-105 
                       hover:shadow-lg hover:shadow-yellow-400/20"
            >
              Write Your Blog
            </button>
          </div>
          <div className="hidden lg:block relative">
            <div className="relative -right-[60px] z-10">
              <img 
                src={RESO3} 
                alt="Blogging" 
                className="w-full max-w-xl mx-auto filter drop-shadow-2xl"
              />
            </div>
            
            {/* Animated Cards */}
            <div className="absolute top-[60px] left-0 space-y-4">
              {[
                { color: 'bg-yellow-400', time: '3min' },
                { color: 'bg-blue-400', time: '2min' },
                { color: 'bg-red-400', time: '1min' }
              ].map((card, index) => (
                <div
                  key={index}
                  className="w-80 bg-white rounded-xl shadow-xl transform transition-all duration-300 hover:scale-105 animate-float"
                  style={{
                    animationDelay: `${index * 0.2}s`,
                    transform: `rotate(${index * 2 - 4}deg)`
                  }}
                >
                  <div className="flex items-center p-4 gap-3">
                    <div className={`w-8 h-8 rounded-full ${card.color}`}></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                    </div>
                    <div className="text-sm text-gray-400">{card.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-1/2 w-full max-w-2xl px-8">
          <div className="relative bg-white rounded-2xl shadow-xl">
            <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="What would you like to learn today?"
              className="w-full py-6 pl-16 pr-6 text-lg rounded-2xl border-none focus:ring-2 focus:ring-yellow-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Blog Posts Grid - White Background */}
      <section className="bg-white w-full pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {isLoading ? (
              [...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white rounded-xl overflow-hidden shadow-lg w-full">
                  <div className="h-48 bg-gray-200 w-full" />
                  <div className="p-6">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4" />
                    <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              ))
            ) : (
              posts.map((post) => (
                <article
                  key={post._id}
                  className="bg-white rounded-xl overflow-hidden shadow-lg transition-transform hover:-translate-y-1 w-full"
                >
                  <div className="relative h-48 w-full">
                    <img
                      src={post.coverImage || image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-3">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.brief}
                    </p>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <Calendar size={16} className="mr-2" />
                          {new Date(post.dateAdded).toLocaleDateString()}
                        </span>
                        <span className="flex items-center">
                          <Clock size={16} className="mr-2" />
                          {post.readTime} min read
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(`/blog/${post.slug}`)}
                      className="button-custom w-full"
                    >
                      <span>Read More</span>
                      <ChevronRight size={16} className="icon" />
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
          
        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
            <div className="flex items-center justify-center space-x-8 mt-16">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="button-custom"
              >
                <span>Previous</span>
              </button>
              <span className="text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="button-custom"
              >
                <span>Next</span>
              </button>
            </div>
          )}
        </div>
      </section>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default BlogList;